import {
    QuestionnaireGroup,
    QuestionnaireSubGroup,
    QuestionnaireColumnInformation,
    QuestionnaireColumnSourceOverride,
    questionnaireColumnsMapping,
    QuestionnaireOperationGroup,
    QuestionnaireOperation,
    QuestionnaireOperationGroupType,
    QuestionnaireOperationSortType,
    QuestionnaireOperationGroupFormattingType,
    QuestionnaireResponseScope,
} from '@mona/models';

/**
 * API QuestionnaireGroup model (snake_case from backend)
 */
export interface ApiQuestionnaireGroup {
    id: string;
    order?: number;
    is_visible?: boolean;
    navigation_title: string;
    navigation_icon?: string | null;
    navigation_url?: string | null;
    page_title: string;
    subgroups?: ApiQuestionnaireSubGroup[];
    operation_groups?: ApiOperationGroup[];
}

/**
 * API QuestionnaireSubGroup model
 */
export interface ApiQuestionnaireSubGroup {
    id: string;
    questionnaire_group_id?: string;
    order?: number;
    display_name: string;
    is_visible?: boolean;
    questionnaire_button_text?: string | null;
    icon?: string | null;
    tag?: string | null;
    response_scope?: QuestionnaireResponseScope | null;
    column_informations?: ApiQuestionnaireColumnInformation[];
    operation_groups?: ApiOperationGroup[];
}

/**
 * API QuestionnaireColumnInformation model
 */
export interface ApiQuestionnaireColumnInformation {
    id: string;
    sub_group_id?: string;
    order?: number;
    display_name: string;
    source_key: string;
    value_type: string | string[];
    fallback_text?: string | null;
    is_link_id?: boolean;
    is_visible?: boolean;
    /** BE may send `""` for unset */
    entity_type?: 'questionnaire' | 'questionnaireResponse' | '' | null;
    columns_mapping?: ApiQuestionnaireColumnValue[];
    custom_formatting?: Record<string, any> | null;
    source_overrides?: ApiQuestionnaireColumnSourceOverride[];
}

/**
 * API per-questionnaire column source override
 */
export interface ApiQuestionnaireColumnSourceOverride {
    id?: string;
    questionnaire_url: string;
    source_key: string;
    is_link_id?: boolean;
}

/**
 * API QuestionnaireColumnValue model
 */
export interface ApiQuestionnaireColumnValue {
    id?: string;
    display_name: string;
    fhir_value: string;
    column_id?: string;
    is_visible?: boolean;
}

/**
 * API OperationGroup model
 */
export interface ApiOperationGroup {
    id: string;
    questionnaire_sub_group_id?: string;
    order?: number;
    display_name: string;
    is_visible?: boolean;
    group_type: string;
    behavior_type?: string | null;
    is_autofill?: boolean;
    display_names_mapping?: Record<string, string> | null;
    operations?: ApiOperation[];
}

/**
 * API Operation model
 */
export interface ApiOperation {
    id: string;
    operation_group_id?: string;
    order?: number;
    display_name: string;
    is_visible?: boolean;
    is_default?: boolean;
    source_key?: string | null;
    fhir_value?: any;
    value_type?: string | null;
    is_link_id?: boolean;
    /** BE may send `""` for unset */
    entity_type?: 'questionnaire' | 'questionnaireResponse' | '' | null;
    behavior_type?: string | null;
    sort_type?: string;
    group_formatting_type?: 'string' | 'date';
    display_names_mapping?: Record<string, string> | null;
}

/**
 * Transform API column value to frontend model
 */
export function transformApiColumnValue(apiValue: ApiQuestionnaireColumnValue): questionnaireColumnsMapping {
    return {
        displayName: apiValue.display_name,
        fhirValue: apiValue.fhir_value,
        isVisible: apiValue.is_visible ?? true,
    };
}

/**
 * Transform API column source override to frontend model
 */
export function transformApiColumnSourceOverride(
    apiOverride: ApiQuestionnaireColumnSourceOverride,
): QuestionnaireColumnSourceOverride {
    return {
        id: apiOverride.id,
        questionnaireUrl: apiOverride.questionnaire_url,
        sourceKey: apiOverride.source_key,
        isLinkId: apiOverride.is_link_id ?? false,
    };
}

/** Django sometimes serializes missing values as the string `"null"` */
function normalizeNullValue<T>(value: T | null | undefined): NonNullable<T> | null {
    if (value === 'null' || value == null) {
        return null;
    }
    return value as NonNullable<T>;
}

function normalizeEntityType(value: string | null | undefined): 'questionnaire' | 'questionnaireResponse' {
    return value === 'questionnaire' ? 'questionnaire' : 'questionnaireResponse';
}

function normalizeCustomFormatting(value: Record<string, any> | null | undefined): Record<string, any> | undefined {
    if (!value || Object.keys(value).length === 0) {
        return undefined;
    }
    return value;
}

/**
 * Transform API column information to frontend model
 */
export function transformApiColumnInformation(
    apiColumn: ApiQuestionnaireColumnInformation,
): QuestionnaireColumnInformation {
    return {
        order: apiColumn.order ?? 0,
        displayName: apiColumn.display_name,
        sourceKey: apiColumn.source_key,
        valueType: apiColumn.value_type,
        fallbackText: apiColumn.fallback_text ?? '',
        isLinkId: apiColumn.is_link_id ?? false,
        isVisible: apiColumn.is_visible ?? true,
        entityType: normalizeEntityType(apiColumn.entity_type),
        questionnaireColumnsMapping: (apiColumn.columns_mapping ?? []).map(transformApiColumnValue),
        customFormatting: normalizeCustomFormatting(apiColumn.custom_formatting),
        sourceOverrides: (apiColumn.source_overrides ?? []).map(transformApiColumnSourceOverride),
    };
}

export function transformApiOperation(apiOperation: ApiOperation): QuestionnaireOperation {
    return {
        order: apiOperation.order ?? 0,
        displayName: apiOperation.display_name,
        isVisible: apiOperation.is_visible ?? true,
        isDefault: apiOperation.is_default ?? false,
        sourceKey: normalizeNullValue(apiOperation.source_key),
        fhirValue: normalizeNullValue(apiOperation.fhir_value),
        valueType: normalizeNullValue(apiOperation.value_type),
        isLinkId: apiOperation.is_link_id ?? false,
        entityType: normalizeEntityType(apiOperation.entity_type),
        behaviorType: normalizeNullValue(apiOperation.behavior_type),
        // sort_type kept only as optional legacy fallback; prefer Operation.behavior_type
        sortType: apiOperation.sort_type as QuestionnaireOperationSortType | undefined,
        groupFormattingType: apiOperation.group_formatting_type,
        displayNamesMapping: apiOperation.display_names_mapping || undefined,
    };
}

/**
 * Map behavior_type → operation groupFormattingType when BE omits group_formatting_type
 */
function mapBehaviorToGroupFormatting(
    behaviorType?: string | null,
): QuestionnaireOperationGroupFormattingType | undefined {
    switch (behaviorType) {
        case 'date_exact':
        case 'datetime_round_day':
        case 'datetime_round_hour':
            return QuestionnaireOperationGroupFormattingType.Date;
        case 'string_normalized':
        case 'exact_match':
            return QuestionnaireOperationGroupFormattingType.String;
        default:
            return undefined;
    }
}

/**
 * Transform API operation group to frontend model.
 * Group/Sort: Operation.behavior_type wins; fall back to OperationGroup.behavior_type.
 */
export function transformApiOperationGroup(apiGroup: ApiOperationGroup): QuestionnaireOperationGroup {
    const groupBehaviorType = normalizeNullValue(apiGroup.behavior_type);
    const groupType = apiGroup.group_type as QuestionnaireOperationGroupType;
    const canInheritBehavior =
        groupType === QuestionnaireOperationGroupType.Group || groupType === QuestionnaireOperationGroupType.Sort;

    return {
        order: apiGroup.order ?? 0,
        displayName: apiGroup.display_name,
        isVisible: apiGroup.is_visible ?? true,
        groupType,
        isAutofill: apiGroup.is_autofill,
        behaviorType: groupBehaviorType,
        operations: (apiGroup.operations ?? [])
            .map(apiOperation => {
                const operation = transformApiOperation(apiOperation);

                if (canInheritBehavior) {
                    operation.behaviorType = operation.behaviorType ?? groupBehaviorType;
                    operation.groupFormattingType =
                        operation.groupFormattingType ?? mapBehaviorToGroupFormatting(operation.behaviorType);
                }

                return operation;
            })
            .sort((a, b) => a.order - b.order),
    };
}

/**
 * Transform API subgroup to frontend model
 */
export function transformApiSubGroup(apiSubGroup: ApiQuestionnaireSubGroup): QuestionnaireSubGroup {
    const responseScope: QuestionnaireResponseScope =
        apiSubGroup.response_scope === 'patient' ? 'patient' : 'encounter';

    return {
        order: apiSubGroup.order ?? 0,
        displayName: apiSubGroup.display_name,
        isVisible: apiSubGroup.is_visible ?? true,
        questionnaireButtonText: apiSubGroup.questionnaire_button_text ?? '',
        icon: normalizeNullValue(apiSubGroup.icon),
        tag: apiSubGroup.tag ?? '',
        responseScope,
        questionnaireColumnInformations: (apiSubGroup.column_informations ?? [])
            .map(transformApiColumnInformation)
            .sort((a, b) => a.order - b.order),
        operationGroups: (apiSubGroup.operation_groups ?? [])
            .map(transformApiOperationGroup)
            .sort((a, b) => a.order - b.order),
    };
}

/**
 * Normalize navigation URL to extract the route slug
 * Converts "/questionnaires/clinical" to "clinical"
 */
function normalizeNavigationUrl(url: string | null | undefined): string {
    if (!url) {
        return '';
    }
    // Remove leading slash and extract the last segment as the route slug
    const segments = url.replace(/^\/+/, '').split('/');
    return segments[segments.length - 1] || '';
}

export function transformApiQuestionnaireGroup(apiGroup: ApiQuestionnaireGroup): QuestionnaireGroup {
    return {
        id: apiGroup.id,
        isVisible: apiGroup.is_visible ?? true,
        navigationTitle: apiGroup.navigation_title,
        navigationIcon: normalizeNullValue(apiGroup.navigation_icon),
        pageTitle: apiGroup.page_title,
        order: apiGroup.order ?? 0,
        navigationUrl: normalizeNavigationUrl(apiGroup.navigation_url),
        operationGroups: (apiGroup.operation_groups ?? []).map(transformApiOperationGroup),
        questionnaireSubGroups: (apiGroup.subgroups ?? []).map(transformApiSubGroup).sort((a, b) => a.order - b.order),
    };
}

export function transformApiQuestionnaireGroups(
    apiGroups: ApiQuestionnaireGroup[] | null | undefined,
): QuestionnaireGroup[] {
    if (!Array.isArray(apiGroups)) {
        return [];
    }
    return apiGroups.map(transformApiQuestionnaireGroup);
}
